import { useEffect, useState, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { isEqual } from "lodash"

import BasicButton from "@/components/button/BasicButton"
import TextField from "@/components/form/textField/TextField"
import IconButton from "@/components/button/IconButton"
import Selector from "@/components/form/selector/Selector"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import { CustomPlayerMetric } from "@/types/metric"
import { Action, ReadActionsResponse } from "@/types/setting"
import { ReadSpeedZonesResponse } from "@/types/zone"
import { ActionSettingType } from "@/constants/unit"
import { fixedNumberDropZero, convertUnitSquare } from "@/utils/helper"
import { useUnitQuery } from "@/hooks/api/useSettingQuery"
import { DEFAULT_TYPE_UNIT, NEW_TYPE } from "@/app/settings/metric/zones/components/constants"
import { getThresholdRangeText } from "@/app/settings/metric/actions/thresholdRange"
import { observeMuiErrors } from "@/utils/error"
import { createActionSettingState } from "@/stores/settings"

const getDisplayZoneCode = (code: string) => {
  const match = code.match(/^(.+?)(\d+)$/)
  if (match) {
    const [, prefix, num] = match

    return `${prefix}${parseInt(num) + 1}`
  }

  return code
}

const ZONE_TYPE_BY_ACTION = {
  [ActionSettingType.RUN]: "speed",
  [ActionSettingType.ACCELERATION]: "acceleration",
  [ActionSettingType.DECELERATION]: "deceleration",
} as const

export default function ActionSettingTab({
  type,
  initialData,
  zones: zonesData,
  editedData,
  setEditedData,
}: {
  type: ActionSettingType.RUN | ActionSettingType.ACCELERATION | ActionSettingType.DECELERATION
  initialData: ReadActionsResponse | undefined
  zones: ReadSpeedZonesResponse | undefined
  editedData: ReadActionsResponse | null
  setEditedData: (state: ReadActionsResponse) => void
}) {
  const { t } = useTranslation()
  const { setIsAllValid, setHasChanges } = createActionSettingState()
  const { data: units } = useUnitQuery()

  // Threshold 구간 값 표시용 단위·자릿수 — Zone 설정 화면(ZoneTabContent)과 같은 규칙 (#171)
  const zoneUnit = useMemo(() => {
    const zoneType = ZONE_TYPE_BY_ACTION[type]
    const found = units?.find((unit) => unit.type === NEW_TYPE[zoneType])

    return {
      unit: convertUnitSquare(found?.unit || DEFAULT_TYPE_UNIT[zoneType]),
      decimalPoint: found?.value ?? 1,
    }
  }, [units, type])

  const [originActions, setOriginActions] = useState<Action[]>([])
  const [actionData, setActionData] = useState<Action[]>([])
  const [tempActionName, setTempActionName] = useState<string>("")
  const [zones, setZones] = useState<CustomPlayerMetric[]>([])
  const [isEditActionName, setIsEditActionName] = useState([false])
  const [isMuiError, setIsMuiError] = useState(false)
  const [lastTouched, setLastTouched] = useState<
    { index: number; type: "tolerance" | "duration" } | undefined
  >()

  useEffect(() => {
    const observer = observeMuiErrors((hasErr) => setIsMuiError(hasErr))

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (initialData) {
      setOriginActions(initialData.global || [])
      setIsEditActionName(new Array(initialData.global?.length || 0).fill(false))
    }
  }, [initialData])

  useEffect(() => {
    if (editedData) {
      setActionData(editedData.global || [])
    } else if (initialData) {
      setActionData(initialData.global || [])
    }
  }, [editedData, initialData])

  useEffect(() => {
    const globalZones = zonesData?.global || []

    setZones(
      globalZones.map((glob) => ({
        ...glob,
        text: t(getDisplayZoneCode(glob.code ?? "")),
      })) || [],
    )
  }, [zonesData, t])

  useEffect(() => {
    if (actionData.length > 0) {
      setEditedData({ global: actionData })
      setIsAllValid(type, !isErr())
    }
  }, [actionData])

  const isErr = useCallback(() => {
    const newActionData = actionData.map((action) => ({
      ...action,
      duration: fixedNumberDropZero(Number(action.duration)),
      tolerance: fixedNumberDropZero(Number(action.tolerance)),
    }))

    const equal = isEqual(
      newActionData.sort((a, b) => a.actionCode.localeCompare(b.actionCode)),
      originActions?.sort((a, b) => a.actionCode.localeCompare(b.actionCode)),
    )
    setHasChanges(type, !equal)

    const numeric =
      actionData.every((data) => !isNaN(Number(data.tolerance))) &&
      actionData.every((data) => !isNaN(Number(data.duration)))

    const underZero = actionData.some(
      (data) => Number(data.tolerance) < 0 || Number(data.duration) <= 0,
    )

    const emptyDuration = actionData.some((data) => !data.duration && data.duration !== 0)
    const emptyTolerance = actionData.some((data) => !data.tolerance && data.tolerance !== 0)

    return !numeric || underZero || emptyDuration || emptyTolerance || isMuiError
  }, [actionData, originActions, isMuiError])

  const getIsErrorWithName = (actionName: string) => {
    return !actionName || actionName.length > 35
  }

  return (
    <>
      {actionData.map((data, index) => (
        <div className="ActionRowCont" key={data.actionCode}>
          <div className="row-title-editable">
            <p className="row-title-default-label">{`${t(type)} ${t("effort")} ${data.actionCode.slice(-1)}`}</p>
            {isEditActionName[index] ? (
              <div className="row-title-edit-mode">
                <TextField
                  value={tempActionName}
                  onChange={(e) => {
                    setTempActionName(e.target.value)
                  }}
                  error={getIsErrorWithName(tempActionName)}
                  showMessage={getIsErrorWithName(tempActionName)}
                  message={
                    !data.actionName
                      ? t("rules.required")
                      : t("rules.max_len", { name: t("actionName"), max_len: 35 })
                  }
                />
                <BasicButton
                  type="primary-outlined"
                  size="md"
                  onClick={() => {
                    setActionData((prev) => [
                      ...prev.slice(0, index),
                      {
                        ...data,
                        actionName: tempActionName,
                      },
                      ...prev.slice(index + 1),
                    ])
                    setIsEditActionName(
                      isEditActionName.map((origin, i) => (i === index ? false : origin)),
                    )
                  }}
                  disabled={getIsErrorWithName(tempActionName)}>
                  {t("button.done")}
                </BasicButton>
              </div>
            ) : (
              <div className="row-title-label">
                <p>{t(data.actionName)}</p>
                <IconButton
                  type="ghost"
                  icon="pencil"
                  size="xs"
                  className="row-title-edit-icon"
                  onClick={() => {
                    setIsEditActionName(
                      isEditActionName.map((origin, i) => (i === index ? true : origin)),
                    )
                    setTempActionName(data.actionName)
                  }}
                />
              </div>
            )}
          </div>

          <div className="ActionRowItemCont">
            <div className="action-threshold">
              <p className="action-form-label">{t("threshold")}</p>
              <div>
                <Selector
                  type="autocomplete"
                  value={zones?.find((zone) => zone.code === data.thresholdZoneCode) ?? ""}
                  options={zones}
                  onChange={(_: unknown, code: CustomPlayerMetric) => {
                    setActionData((prev) => [
                      ...prev.slice(0, index),
                      {
                        ...data,
                        thresholdZoneCode: code.code,
                      },
                      ...prev.slice(index + 1),
                    ])
                  }}
                  renderOption={(props, option) => {
                    const { key, ...newProps } = props

                    return (
                      <li key={(data.actionCode + option.code) as string} {...newProps}>
                        {option.text}{" "}
                        {data.thresholdZoneCode === option.code && (
                          <Icon type="check" size={16} color={colors.neutral.grey[1000]} />
                        )}
                      </li>
                    )
                  }}
                  labelKey="text"
                />
                {(() => {
                  const rangeText = getThresholdRangeText(
                    zones,
                    data.thresholdZoneCode,
                    zoneUnit.unit,
                    zoneUnit.decimalPoint,
                  )

                  return rangeText ? <p className="action-threshold-range">{rangeText}</p> : null
                })()}
              </div>
            </div>

            <div className="action-inputs-wrapper">
              <div className="action-duration">
                <p className="action-form-label">
                  {t("duration")} ({t("sec")})
                </p>
                <TextField
                  className="action-duration-text-field"
                  type="number"
                  slotProps={{
                    htmlInput: {
                      step: 0.1,
                    },
                  }}
                  value={data.duration ?? 0}
                  onChange={(event) => {
                    setActionData((prev) => {
                      return [
                        ...prev.slice(0, index),
                        {
                          ...data,
                          duration: Number(event.target.value),
                        },
                        ...prev.slice(index + 1),
                      ]
                    })
                  }}
                  onBlur={(event) => {
                    setActionData((prev) => {
                      return [
                        ...prev.slice(0, index),
                        {
                          ...data,
                          duration: Math.round(Number(event.target.value) * 10) / 10,
                        },
                        ...prev.slice(index + 1),
                      ]
                    })
                    setLastTouched(undefined)
                  }}
                  onFocus={() => {
                    setLastTouched({ index, type: "duration" })
                  }}
                  error={data.duration < 0.1 || data.duration >= 10}
                  showMessage={
                    (data.duration < 0.1 || data.duration >= 10) &&
                    lastTouched?.index === index &&
                    lastTouched.type === "duration"
                  }
                  message={
                    data.duration < 0.1
                      ? t("rules.min", { name: t("duration"), min: 0.1 })
                      : data.duration >= 10
                        ? t("rules.max", { name: t("duration"), max: 9.9 })
                        : ""
                  }
                />
              </div>
              <div className="action-tolerance">
                <p className="action-form-label">
                  {t("tolerance")} ({t("sec")})
                </p>
                <TextField
                  className="action-duration-text-field"
                  type="number"
                  slotProps={{
                    htmlInput: {
                      step: 0.1,
                    },
                  }}
                  value={data.tolerance ?? 0.4}
                  onChange={(event) => {
                    setActionData((prev) => {
                      return [
                        ...prev.slice(0, index),
                        {
                          ...data,
                          tolerance: Number(event.target.value),
                        },
                        ...prev.slice(index + 1),
                      ]
                    })
                  }}
                  onBlur={(event) => {
                    setActionData((prev) => {
                      return [
                        ...prev.slice(0, index),
                        {
                          ...data,
                          tolerance: Math.round(Number(event.target.value) * 10) / 10,
                        },
                        ...prev.slice(index + 1),
                      ]
                    })
                    setLastTouched(undefined)
                  }}
                  onFocus={() => {
                    setLastTouched({ index, type: "tolerance" })
                  }}
                  error={data.tolerance < 0 || data.tolerance > 1}
                  showMessage={
                    (data.tolerance < 0 || data.tolerance > 1) && lastTouched?.index === index
                  }
                  message={
                    data.tolerance < 0
                      ? t("rules.min", { name: t("tolerance"), min: 0 })
                      : data.tolerance > 1
                        ? t("rules.max", { name: t("tolerance"), max: 1 })
                        : ""
                  }
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  )
}
